import { Domain } from 'library/ts/model/enums/web/Domain';
import { getSmartTagRuntimeVO } from 'tag/ts/smart_tag/utilities/Runtime';
import { AbstractFreewheelClass } from 'tag/ts/tag/ads/freewheel/abstract/AbstractFreewheelClass';
import { IFWSiteDAIAPIParams } from 'tag/ts/tag/interfaces/ads/freewheel/IFWSiteDAIAPIParams';
import { IFWSiteDAIParams } from 'tag/ts/tag/interfaces/ads/freewheel/IFWSiteDAIParams';
import { IFWSiteIMAParams } from 'tag/ts/tag/interfaces/ads/freewheel/IFWSiteIMAParams';
import { IFWSiteLinearParams } from 'tag/ts/tag/interfaces/ads/freewheel/IFWSiteLinearParams';
import { IFWPauseAdSiteParams } from 'tag/ts/tag/interfaces/ads/pause/IFWPauseAdSiteParams';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { TFWSiteParams } from 'tag/ts/tag/types/ads/freewheel/TFWSiteParams';

/**
 * @copyright © Paramount Streaming
 * @description Abstract class for Freewheel site parameters
 */
export abstract class AbstractFWSiteParams extends AbstractFreewheelClass {

	/**
	 * @description Internal store for site ad parameters
	 * @type {object}
	 * @readonly
	 */
	protected readonly abstract adParams: object;

	/**
	 * @description Potential keys in FMS response which should NOT be passed to Freewheel ad call from Desktop.
	 */
	protected readonly excludedFMSKeys = [
		'_fw_app_bundle',
		'_fw_atts',
		'_fw_did',
		'_fw_is_lat',
		'_fw_nielsen_app_id',
		'fms_idfv',
		'fms_ifa',
		'imafw__fw_app_bundle',
		'imafw__fw_atts',
		'imafw__fw_did',
		'imafw__fw_is_lat'
	];

	protected constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				name: 'AbstractFWSiteParams',
				...argument
			}
		);
	}

	/**
	 * @description Returns site parameters
	 * @returns {object}
	 */
	get adParameters(): object {
		return this.adParams;
	}

	/**
	 * Creates site parameters for ads using linear ad tracking
	 * @returns {IFWSiteLinearParams}
	 * @protected
	 */
	protected createLinearParams(): IFWSiteLinearParams {
		const paramsVO = this?.tag?.params;
		const fmsResponse = paramsVO?.AD?.FW?.FMS;
		const gdprResponse = paramsVO?.AD?.FW?.GDPR;
		const isInternational = this.tag?.tagUtilities?.isInternationalContext( this.tag ) === true;
		const daiPrefix = 'imafw_';

		const fmsValues = {};
		for ( const k in fmsResponse ) {
			if ( k.indexOf( 'fms_' ) > -1 ) {
				// remove imfaw_ prefix for CLID3 tracking
				const cleanKey = k.startsWith( daiPrefix ) ? k.substring( daiPrefix.length ) : k;
				fmsValues[ cleanKey ] = fmsResponse[ k ];
			}
		}

		const params: IFWSiteLinearParams = {
			_fw_nielsen_app_id: 'P0C0C37AD-20C4-4EF7-AF25-BEBCB16DF85E',
			_fw_hylda: 'acid=tpplushylda&aiid=[AIID]&abid=break://[AIID]/[ABID]',
			_fw_vcid2: fmsResponse?._fw_vcid2,
			_fw_player_height: '1080',
			_fw_player_width: '1920',
			_fw_coppa: '0',
			_fw_us_privacy: isInternational ? undefined : paramsVO?.USER?.usPrivacy,
			_fw_gdpr: isInternational ? gdprResponse?._fw_gdpr?.toString() ?? fmsResponse?.imafw__fw_gdpr : undefined,
			_fw_gdpr_consent: isInternational ? gdprResponse?._fw_gdpr_consent?.toString() ?? fmsResponse?.imafw__fw_gdpr_consent : undefined,
			...fmsValues
		};
		this?.logger?.log( 'createLinearParams:', params );
		return params;
	}

	/**
	 * Creates site parameters for ads using IMA
	 * @returns {IFWSiteIMAParams}
	 * @protected
	 */
	protected createIMAParams(): IFWSiteIMAParams {
		const paramsVO = this?.tag?.params;
		const siteSessionData = this?.tag?.model?.siteUtilities?.siteSessionData;
		const metadata = this?.tag?.model?.apiMetadata;
		const utils = window.SmartTag.utilities;
		const fmsResponse = paramsVO?.AD?.FW?.FMS;
		const gdprResponse = paramsVO?.AD?.FW?.GDPR;
		const runtimeVO = getSmartTagRuntimeVO();
		const isPPlus = !!runtimeVO.top.parsed.host.includes( Domain.PPLUS );
		const coppa = ( !!isPPlus && !utils.Type.isEmpty( siteSessionData?.tfcd ) ) ? siteSessionData.tfcd.toString() : undefined;
		const isLive = !!this?.tag?.model?.contentUtilities?.isLive( paramsVO, metadata );

		const params: IFWSiteIMAParams = {
			_fw_ae: paramsVO?.USER?.mvpdId,
			_fw_continuous_play: !!isLive ? undefined : siteSessionData?.cpSession,
			_fw_coppa: coppa,
			_fw_vcid2: fmsResponse?._fw_vcid2,
			_fw_gdpr: gdprResponse?._fw_gdpr?.toString(),
			_fw_gdpr_consent: gdprResponse?._fw_gdpr_consent?.toString(),
			region: siteSessionData?.region
		};
		this?.logger?.log( 'createIMAParams:', params );
		return params;
	}

	/**
	 * Creates site parameters for ads using DAI SDK
	 * @returns {IFWSiteDAIParams}
	 * @protected
	 */
	protected createDAISDKParams(): IFWSiteDAIParams {
		const paramsVO = this?.tag?.params;
		const siteSessionData = this?.tag?.model?.siteUtilities?.siteSessionData;
		const metadata = this?.tag?.model?.apiMetadata;
		const utils = window.SmartTag.utilities;
		const fmsResponse = paramsVO?.AD?.FW?.FMS;
		const gdprResponse = paramsVO?.AD?.FW?.GDPR;
		const runtimeVO = getSmartTagRuntimeVO();
		const isPPlus = !!runtimeVO.top.parsed.host.includes( Domain.PPLUS );
		const coppa = ( !!isPPlus && !utils.Type.isEmpty( siteSessionData?.tfcd ) ) ? siteSessionData.tfcd.toString() : undefined;
		const isLive = !!this?.tag?.model?.contentUtilities?.isLive( paramsVO, metadata );
		const isIntl = this.tag.tagUtilities.isInternationalContext( this?.tag );

		const params: IFWSiteDAIParams = {
			imafw__fw_ae: paramsVO?.USER?.mvpdId,
			imafw__fw_vcid2: fmsResponse?._fw_vcid2,
			imafw__fw_us_privacy: paramsVO?.USER?.usPrivacy,
			imafw__fw_gdpr: gdprResponse?._fw_gdpr?.toString(),
			imafw__fw_gdpr_consent: gdprResponse?._fw_gdpr_consent?.toString(),
			imafw_region: siteSessionData?.region
		};
		if ( !isLive ) {
			params.imafw__fw_continuous_play = siteSessionData?.cpSession;
			params.imafw__fw_coppa = coppa;
		}
		if ( isIntl ) {
			params.imafw__fw_is_lat = siteSessionData?._fw_is_lat;
		}
		this?.logger?.log( 'createDAISDKParams:', params );
		return params;
	}

	/**
	 * Creates site parameters for ads using DAI SSB
	 * @returns {IFWSiteDAIParams}
	 * @protected
	 */
	protected createDAISSBParams(): IFWSiteDAIParams {
		const paramsVO = this?.tag?.params;
		const siteSessionData = this?.tag?.model?.siteUtilities?.siteSessionData;
		const utils = window.SmartTag.utilities;
		const fmsResponse = paramsVO?.AD?.FW?.FMS;
		const gdprResponse = paramsVO?.AD?.FW?.GDPR;
		const runtimeVO = getSmartTagRuntimeVO();
		const isPPlus = !!runtimeVO.top.parsed.host.includes( Domain.PPLUS );
		const coppa = ( !!isPPlus && !utils.Type.isEmpty( siteSessionData?.tfcd ) ) ? siteSessionData.tfcd.toString() : undefined;
		const isIntl = this.tag.tagUtilities.isInternationalContext( this?.tag );

		const params: IFWSiteDAIParams = {
			imafw__fw_ae: paramsVO?.USER?.mvpdId,
			imafw__fw_coppa: coppa,
			imafw__fw_vcid2: fmsResponse?._fw_vcid2,
			imafw__fw_us_privacy: paramsVO?.USER?.usPrivacy,
			imafw__fw_gdpr: gdprResponse?._fw_gdpr?.toString(),
			imafw__fw_gdpr_consent: gdprResponse?._fw_gdpr_consent?.toString()
		};
		if ( isIntl ) {
			params.imafw__fw_is_lat = siteSessionData?._fw_is_lat;
		}
		this?.logger?.log( 'createDAISSBParams:', params );
		return params;
	}

	/**
	 * Creates site parameters for ads using DAI API
	 * @returns {IFWSiteDAIAPIParams}
	 * @protected
	 */
	protected createDAIAPIParams(): IFWSiteDAIAPIParams {
		const paramsVO = this?.tag?.params;
		const siteSessionData = this?.tag?.model?.siteUtilities?.siteSessionData;
		const metadata = this?.tag?.model?.apiMetadata;
		const isLive = !!this?.tag?.model?.contentUtilities?.isLive( paramsVO, metadata );

		const params: IFWSiteDAIAPIParams = {
			imafw__fw_app_bundle: 'unknown',
			imafw__fw_continuous_play: !!isLive ? undefined : siteSessionData?.cpSession,
			imafw__fw_coppa: siteSessionData?.tfcd?.toString(),
			imafw__fw_did: 'unknown:54bc9b53-79ac-4946-98ba-2f989e315418',// It should be 'platform:deviceId'.
			imafw__fw_h_referer: 'unknown',
			imafw__fw_is_lat: '0',
			imafw__fw_vcid2: 'b539d5e0d43130d617d64f8991f0a3e0',
			imafw__fw_us_privacy: paramsVO?.USER?.usPrivacy
		};
		this?.logger?.log( 'createDAIAPIParams:', params );
		return params;
	}

	/**
	 * Adds parameters from FMS response to site parameters
	 * @param {TFWSiteParams | IFWPauseAdSiteParams} fwSiteParams
	 * @returns {TFWSiteParams | IFWPauseAdSiteParams}
	 * @protected
	 */
	protected appendFMSData( fwSiteParams: TFWSiteParams | IFWPauseAdSiteParams ): TFWSiteParams | IFWPauseAdSiteParams {
		const fmsResponse = this.tag?.params?.AD?.FW?.FMS;

		if ( this.utils?.Type?.isNullOrUndefined( fmsResponse ) ) {
			this.logger?.log( 'appendFMSData:', 'missing fmsResponse... returning FW site params', '\nparamsVO.AD.FW:', this.tag?.params?.AD?.FW );
			return fwSiteParams;
		}

		const prefix = 'imafw_';
		// filter out any key/values which should not be passed to Freewheel ad call, add remaining key/values to params.
		Object.keys( fmsResponse )
			.filter( ( key ) => !this.excludedFMSKeys.includes( key ) && !fwSiteParams[ key ] )
			.forEach( ( key ) => {
				if ( this.excludedFMSKeys.includes( key ) ) {
					this?.logger?.log( 'appendFMSData:', 'key:', key, 'is excluded from being passed from FMS to FW params' );
					return;
				}

				if ( !this.utils.Type.isEmpty( fwSiteParams[ key ] ) ) {
					this?.logger?.log( 'appendFMSData:', 'key:', key, 'is already set in FW site params, will not override' );
					return;
				}

				const paramsKey = ( key.substring( 0, prefix.length ) !== prefix ) ? prefix + key : key; // prepend prefix to key if it doesn't already exist
				fwSiteParams[ paramsKey ] = fmsResponse[ key ];
			} );

		this.logger?.log( 'appendFMSData:', '\nFMS response:', fmsResponse, '\nparams:', fwSiteParams );
		return fwSiteParams;
	}
}